import { Box, Button, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useRecordContext, useRefresh, useUnselectAll } from "react-admin";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import EventBus from "../../EventBus";
import HubServices from "../../services/HubServices";
import { useAppDispatch } from "../../store/hooks";
import { setCustomSelectOpen } from "../../store/reducers/customSelect";
import MultiResourceSelectDialog from "../Common/CommonDialog/MultiResourceSelectDialog";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import LocalityList from "../Locality/LocalityList";
import CommonListTable, { CommonListColumn } from "../Common/CommonListTable";

const hubsService = new HubServices();

const LocalitiesListOfHub = () => {
    const record = useRecordContext();
    const dispatch = useAppDispatch();
    const refresh = useRefresh();
    const unSelectAll = useUnselectAll("Localities");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loadingData, setLoadingData] = useState(false);

    const handleCityListDialogOpen = () => {
        dispatch(setCustomSelectOpen({ open: true }));
    };

    const postLocalities = async (localities: number[]) => {
        if (localities && localities.length !== 0) {
            try {
                setLoadingData(true);
                const response = await hubsService.addLocalitiesToHub({
                    hubId: record.id,
                    localityIds: localities,
                });
                if (response.status === 200) {
                    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                        message: "Successfully imported selected list",
                        type: CustomSnackbarTypes.SUCCESS,
                    });
                }
                unSelectAll();
                refresh();
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingData(false);
            }
        }
    };

    const deleteSingleLocalityfromHub = useCallback(
        async (id: number) => {
            try {
                const resp = await hubsService.deleteLocalityFromHub(id);
                if (resp.status === 200) {
                    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                        message: "Removed Successfully",
                        type: CustomSnackbarTypes.SUCCESS,
                    });
                }
                refresh();
            } catch (error) {
                console.error(error);
            }
        },
        [refresh],
    );

    const columns: CommonListColumn[] = useMemo(
        () => [
            { label: 'ID', field: 'id' },
            { label: 'Name', render: (row) => row?.localityDetails?.name || '-' },
            { label: 'Locality ID', render: (row) => row?.localityId ?? '-' },
            { label: 'Area ID', render: (row) => row?.localityDetails?.areaId ?? '-' },
            { label: 'Area Name', render: (row) => row?.localityDetails?.areaName || '-' },
            { label: 'City Name', render: (row) => row?.localityDetails?.cityName || '-' },
            { label: 'Rent Paying Capacity', render: (row) => row?.localityDetails?.rentPayingCapacity ?? '-' },
            { label: 'Serviceable', render: (row) => String(row?.localityDetails?.isServiceable ?? '-') },
            {
                label: 'Remove',
                stopRowClick: true,
                render: (row) => (
                    <Button
                        color="error"
                        size="small"
                        onClick={() => deleteSingleLocalityfromHub(row.id as number)}
                    >
                        Remove
                    </Button>
                ),
            },
        ],
        [deleteSingleLocalityfromHub],
    );

    if (!record) return null;

    const localities: any[] = record?.data?.localities ?? [];
    const pagedData = localities.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    return (
        <>
            <MultiResourceSelectDialog
                onClose={(localities: number[]) => postLocalities(localities)}
                isMultiSelect={true}
                name={"Localities"}
                list={LocalityList}
            />

            <Box sx={{ p: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                    <Button variant="contained" disabled={loadingData} onClick={handleCityListDialogOpen}>
                        Add Locality
                    </Button>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 2 }}>
                    <Box>
                        <Typography fontWeight={400} fontSize={'0.75em'} color={'#525252'}>
                            {'Hub Name: '}
                        </Typography>
                        <Typography fontWeight={400} fontSize={'1rem'}>
                            {record?.data?.hubDetails?.name}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography fontWeight={400} fontSize={'0.75em'} color={'#525252'}>
                            {'Hub City Name: '}
                        </Typography>
                        <Typography fontWeight={400} fontSize={'1rem'}>
                            {record?.data?.hubDetails?.cityName}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography fontWeight={400} fontSize={'0.75em'} color={'#525252'}>
                            {'Hub Area Name: '}
                        </Typography>
                        <Typography fontWeight={400} fontSize={'1rem'}>
                            {record?.data?.hubDetails?.areaName}
                        </Typography>
                    </Box>
                </Box>

            <CommonListTable
                columns={columns}
                data={pagedData}
                loading={false}
                page={page}
                rowsPerPage={rowsPerPage}
                total={localities.length}
                onPageChange={setPage}
                onRowsPerPageChange={(rows) => { setRowsPerPage(rows); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                emptyText="No Localities Found"
            />
            </Box>
        </>
    );
};

export default LocalitiesListOfHub;
